/*!
 * ocg-recently-viewed-fix.js  (OCG, Jul 2026)
 * ---------------------------------------------------------------------------
 * SELF-CONTAINED, DEFENSIVE fix for the empty "Recently Viewed Products"
 * carousel on the /cart page of the live theme (163050782939).
 *
 * ROOT CAUSE (not this file's concern to change, only to work around):
 *   sections/recently_viewed.liquid only renders product cards inside
 *   `{%- if sortby == 1 -%}`. The /cart instance of that section is configured
 *   with sortby = "3" (Alphabetically Z-A), so the theme's own section-render
 *   fetch (`/search?section_id=<cartSid>&q=id:X OR id:Y`) returns the swiper
 *   wrapper with ZERO slides. (Older Gecko theme versions of the section had
 *   `if/elsif/else` branches for every sortby value; the swiper rewrite that
 *   ships in the live theme dropped everything except sortby==1 — a regression.)
 *   Writing/accumulating nt_recent works fine.
 *
 * WHAT THIS DOES:
 *   On the cart page only, once the theme has injected the (empty) recently
 *   viewed swiper, it fetches the recently-viewed product cards via the theme's
 *   OWN Section Rendering endpoint and drops the resulting `.swiper-slide`
 *   cards into `.swiper-container-recent .swiper-wrapper`, then refreshes the
 *   existing Swiper instance.
 *     - Source 1 (self-heal): the cart section id itself. If sortby is ever
 *       corrected, this returns real slides and the fallback is skipped.
 *     - Source 2 (fallback): a recently-viewed section instance that renders
 *       with sortby==1 (the product template's RV section) — produces the same
 *       product-grid-item*-recent `.swiper-slide` markup, in nt_recent order.
 *
 * SAFETY: everything is wrapped in try/catch and scoped to the cart page and
 *   the recently-viewed container. A failure can never break the page. Fully
 *   reversible: delete this asset + its <script> line in layout/theme.liquid.
 */
(function () {
  "use strict";
  try {
    var PATH = (window.location && window.location.pathname) || "";
    // Cart page can be /cart or /cart? ... ; also localized (/en/cart). Match "/cart".
    if (!/\/cart(\/|$|\?)/.test(PATH + (window.location.search || ""))) return;

    var CART_LIMIT = 6; // matches the cart section's configured "limit"
    // Known sortby==1 recently-viewed render source(s) used only as a fallback
    // when the cart section itself yields no slides. Adding more ids here is safe.
    var FALLBACK_SIDS = ["template--22671308259547__recently-viewed-products"];
    var SEARCH_URL =
      (window.T4Sroutes && window.T4Sroutes.search_url) ||
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root + "search"
        : "/search");

    function log() {
      /* silent by default; uncomment for debugging
      try { console.log.apply(console, ["[ocg-rv-fix]"].concat([].slice.call(arguments))); } catch (e) {} */
    }

    function getIds() {
      try {
        var raw = localStorage.getItem("nt_recent");
        if (!raw) return [];
        return raw
          .split(",")
          .map(function (s) { return (s || "").trim(); })
          .filter(function (s) { return s && s !== "19041994" && s !== "id:19041994"; });
      } catch (e) {
        return [];
      }
    }

    function fetchSlides(sid, q) {
      var url =
        SEARCH_URL +
        "?section_id=" + encodeURIComponent(sid) +
        "&type=product&options[unavailable_products]=show&q=" + encodeURIComponent(q);
      return fetch(url, { credentials: "same-origin" })
        .then(function (r) { return r.ok ? r.text() : ""; })
        .then(function (html) {
          if (!html) return [];
          var doc = new DOMParser().parseFromString(html, "text/html");
          return [].slice.call(doc.querySelectorAll(".swiper-slide"));
        })
        .catch(function () { return []; });
    }

    function refresh(container) {
      try {
        if (window.T4SThemeSP && typeof window.T4SThemeSP.reinitProductGridItem === "function") {
          window.T4SThemeSP.reinitProductGridItem();
        }
      } catch (e) {}
      try {
        if (window.T4SThemeSP && typeof window.T4SThemeSP.Reveal === "function") {
          window.T4SThemeSP.Reveal();
        }
      } catch (e) {}
      try {
        if (container && container.swiper && typeof container.swiper.update === "function") {
          container.swiper.update();
          if (typeof container.swiper.updateSlides === "function") container.swiper.updateSlides();
        } else if (window.Swiper) {
          /* eslint-disable no-new */
          new window.Swiper(container, {
            loop: false,
            slidesPerView: 2,
            spaceBetween: 16,
            pagination: { el: ".swiper-paginations", clickable: true },
            navigation: { nextEl: ".swiper-button-nexts", prevEl: ".swiper-button-prevs" },
            breakpoints: {
              320: { slidesPerView: 2.2, spaceBetween: 16 },
              768: { slidesPerView: 3.5, spaceBetween: 16 },
              1024: { slidesPerView: 5, spaceBetween: 16 }
            }
          });
        }
      } catch (e) {}
      // nudge lazy-loaders (lazysizes / lazyloadt4s) to unveil card images
      try { window.dispatchEvent(new Event("scroll")); } catch (e) {}
      try { window.dispatchEvent(new Event("resize")); } catch (e) {}
    }

    function populate(wrapper, container) {
      if (!wrapper || wrapper.__ocgRvDone || wrapper.__ocgRvBusy) return;
      if (wrapper.children.length > 0) { wrapper.__ocgRvDone = true; return; }
      var ids = getIds();
      if (!ids.length) { wrapper.__ocgRvDone = true; return; }
      wrapper.__ocgRvBusy = true;
      var q = ids.join(" OR "); // ids are already in "id:NNN" token form

      var sources = [];
      try {
        var w = document.querySelector("#recently_wrap");
        var cartSid = w && w.getAttribute("data-sid");
        if (cartSid) sources.push(cartSid); // primary: self-heal path
      } catch (e) {}
      FALLBACK_SIDS.forEach(function (s) {
        if (sources.indexOf(s) < 0) sources.push(s);
      });

      (function tryNext(i) {
        if (i >= sources.length) { wrapper.__ocgRvDone = true; wrapper.__ocgRvBusy = false; return; }
        fetchSlides(sources[i], q).then(function (slides) {
          if (wrapper.__ocgRvDone || wrapper.children.length > 0) {
            wrapper.__ocgRvDone = true; wrapper.__ocgRvBusy = false; return;
          }
          if (!slides.length) { tryNext(i + 1); return; }
          var added = 0;
          for (var k = 0; k < slides.length && added < CART_LIMIT; k++) {
            try {
              wrapper.appendChild(document.importNode(slides[k], true));
              added++;
            } catch (e) {}
          }
          wrapper.__ocgRvDone = true;
          wrapper.__ocgRvBusy = false;
          log("populated", added, "slides from", sources[i]);
          if (added > 0) refresh(container);
        });
      })(0);
    }

    function scan() {
      var container = document.querySelector(".swiper-container-recent");
      if (!container) return false;
      var wrapper = container.querySelector(".swiper-wrapper");
      if (!wrapper) return false;
      if (wrapper.children.length > 0) return true; // already has slides -> stop watching
      if (wrapper.__ocgRvDone) return true;
      populate(wrapper, container);
      return !!wrapper.__ocgRvDone;
    }

    function start() {
      try {
        if (scan()) return;
        var tries = 0;
        var iv = setInterval(function () {
          tries++;
          if (scan() || tries > 40) { clearInterval(iv); } // poll ~20s max
        }, 500);
        try {
          var mo = new MutationObserver(function () { try { if (scan()) mo.disconnect(); } catch (e) {} });
          mo.observe(document.body, { childList: true, subtree: true });
          setTimeout(function () { try { mo.disconnect(); } catch (e) {} }, 22000);
        } catch (e) {}
      } catch (e) {}
    }

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", start);
    } else {
      start();
    }
  } catch (e) {
    /* never allow this helper to break the page */
  }
})();
